import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const HalleUeberUns = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const versorgungsgebiete = {
    nord: {
      title: 'Stadtbezirk Nord',
      areas: ['Dölau', 'Giebichenstein', 'Heide-Nord/Blumenau', 'Heide-Süd', 'Kröllwitz', 'Lettin', 'Mötzlich']
    },
    ost: {
      title: 'Stadtbezirk Ost',
      areas: ['Büschdorf', 'Dautzsch', 'Diemitz', 'Dieselstraße', 'Freiimfelde/Kanenaer Weg', 'Kanena/Bruckdorf', 'Mötzlich', 'Reideburg', 'Seeben']
    },
    sued: {
      title: 'Stadtbezirk Süd',
      areas: ['Ammendorf/Beesen', 'Böllberg/Wörmlitz', 'Dieselstraße', 'Dölauer Heide', 'Planena', 'Silberhöhe', 'Südstadt']
    },
    west: {
      title: 'Stadtbezirk West',
      areas: ['Böllberg/Wörmlitz', 'Dölau', 'Heide-Nord', 'Nietleben', 'Neustadt', 'Passendorf', 'Saaleaue', 'Seeben', 'Trotha']
    },
    mitte: {
      title: 'Stadtbezirk Mitte',
      areas: ['Altstadt', 'Lutherplatz/Thüringer Bahnhof', 'Paulusviertel', 'Südliche Innenstadt', 'Nördliche Innenstadt']
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-turquoise-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden" data-testid="halle-ueber-uns-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-turquoise-400 to-turquoise-300 opacity-10"></div>
        <div className="container mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Über uns
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Standort <span className="text-turquoise-400 font-bold">Halle (Saale)</span>
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Ambulante Pflege Großstadtherzen® Halle
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong>Wir begannen mit einer einfachen Idee:</strong> Einen Pflegedienst zu gründen, welcher menschlich und ethisch aufgestellt ist und diese Werte auch in der Zukunft stets verfolgt. In Leipzig fing alles im Stadtgebiet Plagwitz an und über die Jahre erweiterten wir unseren Standort bis hin nach Gohlis.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Schlussendlich haben wir nun einen weiteren Standort in der wunderschönen Stadt <strong>Halle an der Saale</strong> eröffnet.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Aus der Sicht der <strong>Großstadtherzen</strong> hat jedes Individuum ein Anrecht auf eine würdevolle und respektierende Pflege und Betreuung, ganz unabhängig von finanziellen Mitteln.
            </p>

            {/* Unser Team */}
            <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Unser Team</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                In Halle steht Ihnen bereits ein Team aus erfahrenen Fachkräften zur Seite – engagiert, einfühlsam und stets für Sie und Ihre Angehörigen da.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Um eine kontinuierliche professionelle Pflege sowie innovative Behandlungsmethoden zu gewährleisten, bilden wir uns regelmäßig fort.
              </p>
            </div>

            {/* Unser Leitbild */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 mb-8 border-l-4 border-green-500">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Unser Leitbild</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <span className="text-green-500 text-2xl w-8 flex-shrink-0">❤️</span>
                  <p className="text-lg text-gray-700">
                    Wir sehen den Klienten nicht nur als Kranken, hilfe- und pflegebedürftigen Menschen sondern als <strong>„Familienmitglied"</strong> des Systems
                  </p>
                </li>
                <li className="flex items-start gap-4">
                  <span className="text-green-500 text-2xl w-8 flex-shrink-0">✓</span>
                  <p className="text-lg text-gray-700">
                    Wir <strong>halten</strong> unsere Zusagen und Versprechen
                  </p>
                </li>
                <li className="flex items-start gap-4">
                  <span className="text-green-500 text-2xl w-8 flex-shrink-0">🤝</span>
                  <p className="text-lg text-gray-700">
                    Wir <strong>achten</strong> unsere Familienmitglieder, unabhängig von deren Nationalität, Konfession, Herkunft, Hautfarbe, Geschlecht, sozialer Schicht, Krankheit oder Behinderung
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Versorgungsgebiete Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Versorgungsgebiete</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Wir sind in folgenden Stadtteilen von Halle (Saale) für Sie da
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.values(versorgungsgebiete).map((bezirk, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-turquoise-50 to-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <h3 className="text-xl font-bold text-turquoise-600 mb-4">{bezirk.title}</h3>
                <ul className="space-y-2">
                  {bezirk.areas.map((area, areaIndex) => (
                    <li key={areaIndex} className="flex items-center gap-2 text-gray-700">
                      <span className="w-2 h-2 bg-turquoise-400 rounded-full"></span>
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MDK Certifications */}
      <section className="py-16 px-4 bg-white" data-testid="halle-mdk-section">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">MDK Prüfungen - Qualitätsnachweis</h2>
          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-2xl shadow-xl p-6 text-center w-full max-w-xs" data-testid="halle-mdk-2026">
              <div className="bg-turquoise-400 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                ✓
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">2026</h3>
              <p className="text-gray-600 mb-2">MDK Prüfung bestanden</p>
              <p className="text-2xl font-bold text-turquoise-400">Note 1,0</p>
            </div>
          </div>
        </div>
      </section>


      {/* Contact Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Kontakt Halle (Saale)</h2>
          <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-3xl shadow-xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Adresse</h3>
                <p className="text-gray-700">
                  Ambulante Pflege Großstadtherzen® Halle UG<br />
                  Merseburger Str. 19<br />
                  06112 Halle (Saale)
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Kontakt</h3>
                <p className="text-gray-700">
                  <a href="tel:034568669465" className="hover:text-turquoise-400 transition-colors font-semibold">Tel: 0345-68669465</a><br />
                  <span className="text-gray-600">Fax: 0345-68669469</span><br />
                  <a href="mailto:info@grossstadtherzen-halle.de" className="hover:text-turquoise-400 transition-colors">info@grossstadtherzen-halle.de</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-turquoise-400 to-turquoise-500 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Lernen Sie uns kennen
          </h2>
          <p className="text-xl mb-8">
            Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch
          </p>
          <Link 
            to="/kontakt"
            className="inline-block bg-white text-turquoise-500 px-8 py-4 rounded-full text-lg font-semibold hover:bg-turquoise-50 transition-all shadow-lg"
            data-testid="halle-ueber-uns-angebot-btn"
          >
            Kontakt aufnehmen
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HalleUeberUns;
